/**
 * A dashboard row's entity name as the same citation chat renders: hover
 * for the card, click to open. Falls back to a plain link for paths the ref
 * parser does not recognise.
 */
import type { FC, MouseEvent, ReactNode } from "react";
import { Link } from "react-router-dom";

import { EntityRef } from "@/features/agents/refs/EntityRef";
import { parseEntityHref } from "@/features/agents/refs/entityRefs";
import { cn } from "@/lib/utils";

import { parseRefMarkdown } from "../format";

type Props = {
  /** Where the row goes. */
  path: string;
  /** The markdown ref, for the hover card's title snapshot. */
  refMarkdown?: string | null;
  className?: string;
  children: ReactNode;
};

export const EntityLink: FC<Props> = ({
  path,
  refMarkdown,
  className,
  children
}) => {
  const target = parseEntityHref(path);
  const title = parseRefMarkdown(refMarkdown)?.title ?? null;
  // Rows are clickable themselves; the link (and its hover card) must not
  // also fire the row.
  const stop = (e: MouseEvent) => e.stopPropagation();
  if (target) {
    return (
      <span onClick={stop} className="inline">
        <EntityRef target={target} title={title} className={className}>
          {children}
        </EntityRef>
      </span>
    );
  }
  return (
    <Link
      to={path}
      className={cn("font-medium hover:underline", className)}
      onClick={stop}
    >
      {children}
    </Link>
  );
};
